import React, { useState, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HelmetProvider } from 'react-helmet-async';
import { LanguageProvider } from './context/LanguageContext';
import { Navigation } from './components/layout/Navigation';
import HeroSection from './components/sections/HeroSection';
import SEO from './components/SEO';
import { SITE_DEFAULT_OG_IMAGE } from './data/projects';
import { organizationSchema, websiteSchema } from './data/siteJsonLd';
import Footer from './components/layout/Footer';
import ContactPush from './components/layout/ContactPush';
import ScrollToTop from './components/ScrollToTop';
import Analytics from './components/Analytics';
import EstimatePage from './pages/EstimatePage';
import './index.css';

const AboutSection = lazy(() => import('./components/sections/AboutSection'));
const TeamSection = lazy(() => import('./components/sections/TeamSection'));
const ProcessSection = lazy(() => import('./components/sections/ProcessSection'));
const EstimatorSection = lazy(() => import('./components/sections/EstimatorSection'));
const ProjectsSection = lazy(() => import('./components/sections/ProjectsSection'));
const ContactSection = lazy(() => import('./components/sections/ContactSection'));
const PortfolioIndex = lazy(() => import('./components/portfolio/PortfolioIndex'));
const PortfolioDetail = lazy(() => import('./components/portfolio/PortfolioDetail'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

const homeJsonLd = [organizationSchema, websiteSchema];

const Loading = () => (
  <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
    <div
      className="h-9 w-9 border border-foreground/15 border-t-foreground/70 rounded-full animate-spin"
      aria-hidden
    />
  </div>
);

const SectionFallback = () => <div className="min-h-[24vh]" aria-hidden />;

function HomePage({ handleHeroIntroComplete }) {
  return (
    <>
      <SEO ogImage={SITE_DEFAULT_OG_IMAGE} canonicalPath="/" jsonLd={homeJsonLd} />
      <HeroSection onHeroIntroComplete={handleHeroIntroComplete} />
      <Suspense fallback={<SectionFallback />}>
        <AboutSection />
        <TeamSection />
        <ProcessSection />
        <EstimatorSection />
        <ProjectsSection />
        <ContactSection />
      </Suspense>
    </>
  );
}

function AppContent({ heroIntroComplete, handleHeroIntroComplete }) {
  const { pathname } = useLocation();
  const isPortfolio = pathname.startsWith('/portfolio');
  const isEstimate = pathname === '/estimate';
  const isLegal = pathname === '/privacy' || pathname === '/terms';
  /** Hero never mounts on portfolio / estimate / legal; logo must still show. */
  const navHeroIntroComplete = isPortfolio || isEstimate || isLegal || heroIntroComplete;
  /** Softer editorial entry into portfolio routes (home → archive). */
  const portfolioMotion = isPortfolio;

  return (
    <>
      <Analytics />
      <ScrollToTop />
      <div className="min-h-screen bg-background">
        <Navigation heroIntroComplete={navHeroIntroComplete} />
        <motion.main
          id="top"
          initial={{ opacity: 0, y: portfolioMotion ? 20 : 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: portfolioMotion ? 0.52 : 0.38,
            ease: [0.16, 1, 0.3, 1],
          }}
          key={pathname}
        >
          <Routes>
            <Route path="/" element={<HomePage handleHeroIntroComplete={handleHeroIntroComplete} />} />
            <Route path="/estimate" element={<EstimatePage />} />
            <Route
              path="/portfolio"
              element={
                <Suspense fallback={<Loading />}>
                  <PortfolioIndex />
                </Suspense>
              }
            />
            <Route
              path="/portfolio/:slug"
              element={
                <Suspense fallback={<Loading />}>
                  <PortfolioDetail />
                </Suspense>
              }
            />
            <Route
              path="/privacy"
              element={
                <Suspense fallback={<Loading />}>
                  <PrivacyPage />
                </Suspense>
              }
            />
            <Route
              path="/terms"
              element={
                <Suspense fallback={<Loading />}>
                  <TermsPage />
                </Suspense>
              }
            />
            <Route
              path="*"
              element={
                <Suspense fallback={<Loading />}>
                  <NotFoundPage />
                </Suspense>
              }
            />
          </Routes>
        </motion.main>
        <Footer />
        <ContactPush />
      </div>
    </>
  );
}

function App() {
  const [heroIntroComplete, setHeroIntroComplete] = useState(() => {
    try {
      return sessionStorage.getItem('satiHeroOk') === '1';
    } catch {
      return false;
    }
  });

  const handleHeroIntroComplete = useCallback(() => {
    setHeroIntroComplete(true);
    try {
      sessionStorage.setItem('satiHeroOk', '1');
    } catch {
      /* ignore */
    }
  }, []);

  return (
    <HelmetProvider>
      <LanguageProvider>
        <BrowserRouter>
          {heroIntroComplete && (
            <style>{`
              [data-testid="hero-logo"] { opacity: 0 !important; pointer-events: none !important; }
              #hero .arch-container [class*="max-w"] > * { opacity: 1 !important; transform: none !important; }
              #hero > div:last-child { opacity: 1 !important; }
            `}</style>
          )}
          <AppContent
            heroIntroComplete={heroIntroComplete}
            handleHeroIntroComplete={handleHeroIntroComplete}
          />
        </BrowserRouter>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
